import { ApolloError } from '@apollo/client';
import { Bar, Justify, Size, Theme } from '@busybusy/webapp-react-ui';
import {
  EquipmentModel,
  EquipmentModelType,
  QueryEquipmentQuery,
  QueryEquipmentQueryVariables,
} from '__generated__/graphql';
import { SIMPLE_EQUIPMENT_QUERY } from 'apollo/queries/equipment-queries';
import { DeleteIcon } from 'assets/icons';
import classNames from 'classnames';
import IconButton from 'components/foundation/buttons/IconButton/IconButton';
import BasicSearchablePicker from 'components/foundation/pickers/basic-picker/BasicSearchablePicker/BasicSearchablePicker';
import useEquipmentModels, { IEquipmentModelFilter } from 'containers/manage-euipment/hooks/useEquipmentModels';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { useToastOpen } from 'contexts/ToastContext';
import { useOpenable } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { t } from 'i18next';
import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';
import { useEffect, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { highlight } from 'utils/stringUtils';
import { uuid } from 'utils/uuidUtils';
import { IBasicPickerProps } from '../../../foundation/pickers/basic-picker/BasicPicker/BasicPicker';
import EquipmentCatMakeModelDeleteProhibitedDialog from '../EquipmentCatMakeModelDeleteProhibitedDialog/EquipmentCatMakeModelDeleteProhibitedDialog';

export type IEquipmentModelPickerProps = Omit<IBasicPickerProps, 'data'> & {
  className?: ClassName;
  equipmentModelType?: EquipmentModelType;
  equipmentCategoryId?: string;
  equipmentMakeId?: string;
  onSelect: (makeId: string | null) => void;
  onExistingModel: (modelValues: IExistingModel) => void;
  onNewModel: (selectedModel: { id: string | null; name: string | null }) => void;
};

interface IModelPickerData {
  id: string;
  name: string;
}

export interface INewModel {
  id: string;
  name: string;
}

export interface IExistingModel {
  id: string;
  categoryId: string;
  makeId: string;
  name: string;
}

const EquipmentModelPicker = ({
  className,
  equipmentModelType,
  equipmentCategoryId,
  equipmentMakeId,
  onSelect,
  onNewModel,
  onExistingModel,
  ...rest
}: IEquipmentModelPickerProps) => {
  const [data, setData] = useState<EquipmentModel[]>([]);
  const [pickerData, setPickerData] = useState<IModelPickerData[]>([]);
  const [error, setError] = useState<string | ApolloError | null | undefined>(null);
  const [modelToDeleteId, setModelToDeleteId] = useState<string>();
  const { getEquipmentModels, deleteEquipmentModel } = useEquipmentModels();
  const toast = useToastOpen();
  const client = useGraphQLClient();

  const deleteDialog = useOpenable();
  const deleteProhibitedDialog = useOpenable();

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentModelType, equipmentCategoryId, equipmentMakeId]);

  const getData = async () => {
    try {
      const filter: IEquipmentModelFilter = {
        equipmentModelType,
        equipmentCategoryId,
        equipmentMakeId,
      };
      const data = await getEquipmentModels(filter);
      setData(data);
      setPickerData(
        uniqBy(
          data?.map((datum) => ({ id: datum.id, name: datum.title! })),
          (model) => model.id
        )
      );
    } catch (error) {
      setError(t('There was an error loading models.'));
    }
  };

  const hasConnectedEquipment = async (modelId: string): Promise<boolean> => {
    if (!isNil(modelId)) {
      const data = await client.request<QueryEquipmentQuery, QueryEquipmentQueryVariables>({
        document: SIMPLE_EQUIPMENT_QUERY,
        variables: {
          first: 1,
          filter: { model: { id: { equal: modelId } }, deletedOn: { isNull: true } },
        },
      });

      return data.equipment?.length ? data.equipment?.length > 0 : false;
    }
    return true;
  };

  const handleDelete = () => {
    if (modelToDeleteId) {
      deleteEquipmentModel(modelToDeleteId).then(() => {
        getData();
        setModelToDeleteId(undefined);
        onSelect(null);
      });
    }
  };

  const setNewModel = (temporaryModel: INewModel) => {
    setPickerData((prevData) => [...prevData, temporaryModel]);

    if (onNewModel) {
      onNewModel({ id: temporaryModel.id, name: temporaryModel.name });
    }

    handleSelect(temporaryModel.id);
  };

  const handleDeleteProhibitedClose = () => {
    deleteProhibitedDialog.close();
    onSelect(null);
  };

  const handleNewModel = (searchValue: string) => {
    if (searchValue.length < 1) {
      toast({ label: t('Model Name is required.'), theme: Theme.DANGER });
    } else {
      const temporaryModel: INewModel = {
        id: uuid(),
        name: searchValue,
      };

      setNewModel(temporaryModel);
    }
  };

  const handleSelect = (selectedId: string | null) => {
    onSelect(selectedId);

    const selectedModel = data.find((model) => model.id === selectedId);

    if (selectedModel && onExistingModel) {
      onExistingModel({
        id: selectedModel.id,
        categoryId: selectedModel.category.id,
        makeId: selectedModel.make.id,
        name: selectedModel.title!,
      });
    }
  };

  const classes = classNames('equipment-model-picker', className);

  const renderCustomRow = (row: IModelPickerData, searchValue: string | undefined) => {
    const selectedModel = data.find((model) => model.id === row.id);

    const deleteClicked = async () => {
      const deleteProhibited = await hasConnectedEquipment(row.id);
      if (deleteProhibited) {
        deleteProhibitedDialog.open();
      } else {
        setModelToDeleteId(row.id);
        deleteDialog.open();
      }
    };

    return (
      <Bar size={Size.SMALL} className={classes} justify={Justify.SPACE_BETWEEN}>
        <div className="ellipsis px-3">
          {searchValue ? highlight(row.name, searchValue) : row.name}
          {selectedModel && (
            <>
              <i>{` ${selectedModel.make.title} `}</i>
              <i>{`(${selectedModel.category.title})`}</i>
            </>
          )}
        </div>
        <IconButton svg={DeleteIcon} onClick={deleteClicked} />
      </Bar>
    );
  };

  return (
    <>
      <BasicSearchablePicker
        onEnter={handleNewModel}
        {...rest}
        renderCustomRow={(row, searchValue) => renderCustomRow(row, searchValue)}
        onSelect={handleSelect}
        data={pickerData}
        className={classes}
        error={!isNil(error) && error !== ''}
        emptyPickerValue={t('Press enter to create a new model')}
      />
      <EquipmentCatMakeModelDeleteProhibitedDialog
        isOpen={deleteProhibitedDialog.isOpen}
        onClose={handleDeleteProhibitedClose}
        type={'model'}
      />
      <DeleteConfirmationDialog
        isOpen={deleteDialog.isOpen}
        title={t('Delete model?')}
        onClose={deleteDialog.close}
        onDelete={handleDelete}
        showHeaderDivider={false}
      >
        {t('If you delete this item it will be gone forever. Are you sure you want to proceed?')}
      </DeleteConfirmationDialog>
    </>
  );
};

export default EquipmentModelPicker;
